import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveCustomerInquiry } from '../../utils/customerDataManager';

// Marble database with comprehensive pricing and specifications
const marbleDatabase = {
  types: {
    'makrana_white': {
      name: 'Makrana White Marble',
      basePrice: 150, // per sq ft
      grades: {
        'premium': { multiplier: 1.5, description: 'Premium quality, minimal veining' },
        'standard': { multiplier: 1.0, description: 'Good quality, moderate veining' },
        'commercial': { multiplier: 0.8, description: 'Commercial grade, higher veining' }
      },
      applications: ['flooring', 'temple', 'kitchen', 'bathroom', 'exterior'],
      thickness: {
        '15mm': { multiplier: 0.8 },
        '18mm': { multiplier: 1.0 },
        '20mm': { multiplier: 1.2 },
        '25mm': { multiplier: 1.5 }
      }
    },
    'makrana_pink': {
      name: 'Makrana Pink Marble',
      basePrice: 180,
      grades: {
        'premium': { multiplier: 1.4, description: 'Premium pink with consistent color' },
        'standard': { multiplier: 1.0, description: 'Good quality pink marble' }
      },
      applications: ['flooring', 'temple', 'decorative'],
      thickness: {
        '18mm': { multiplier: 1.0 },
        '20mm': { multiplier: 1.2 },
        '25mm': { multiplier: 1.5 }
      }
    },
    'makrana_green': {
      name: 'Makrana Green Marble',
      basePrice: 200,
      grades: {
        'premium': { multiplier: 1.3, description: 'Premium green, rare variety' },
        'standard': { multiplier: 1.0, description: 'Standard green marble' }
      },
      applications: ['flooring', 'decorative', 'temple'],
      thickness: {
        '18mm': { multiplier: 1.0 },
        '20mm': { multiplier: 1.2 }
      }
    }
  },
  
  customWork: {
    'temple_carving': {
      name: 'Temple Carving Work',
      basePrice: 2000, // per sq ft
      complexity: {
        'simple': { multiplier: 1.0, description: 'Basic geometric patterns' },
        'medium': { multiplier: 1.8, description: 'Detailed carvings with moderate complexity' },
        'complex': { multiplier: 3.0, description: 'Intricate sculptures and detailed work' }
      }
    },
    'inlay_work': {
      name: 'Marble Inlay Work',
      basePrice: 1500,
      complexity: {
        'simple': { multiplier: 1.0, description: 'Basic floral patterns' },
        'medium': { multiplier: 2.0, description: 'Detailed inlay designs' },
        'complex': { multiplier: 3.5, description: 'Intricate Taj Mahal style inlay' }
      }
    }
  },

  transport: {
    zones: {
      'local': { radius: 50, rate: 15 }, // per sq ft
      'regional': { radius: 200, rate: 25 },
      'national': { radius: 1000, rate: 40 },
      'international': { radius: 9999, rate: 80 }
    }
  },

  labor: {
    'flooring': { rate: 45, unit: 'sq ft' },
    'temple': { rate: 150, unit: 'sq ft' },
    'kitchen': { rate: 60, unit: 'sq ft' },
    'bathroom': { rate: 55, unit: 'sq ft' },
    'exterior': { rate: 50, unit: 'sq ft' }
  },

  projectTimeline: {
    'flooring': { baseTime: 2, unit: 'days per 100 sq ft' },
    'temple': { baseTime: 15, unit: 'days per 100 sq ft' },
    'kitchen': { baseTime: 3, unit: 'days per 100 sq ft' },
    'bathroom': { baseTime: 2, unit: 'days per 100 sq ft' },
    'custom': { baseTime: 20, unit: 'days per 100 sq ft' }
  }
};

const WASTAGE_FACTOR = 1.07;
const GST_RATE = 0.05;

const getVolumeDiscountRate = (area) => {
  if (area >= 2000) return 0.1;
  if (area >= 1000) return 0.07;
  if (area >= 500) return 0.04;
  return 0;
};

const AIMarbleChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentCalculation, setCurrentCalculation] = useState({});
  const [calculationStep, setCalculationStep] = useState('welcome');
  const [userProfile, setUserProfile] = useState({});
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage("🙏 **Namaste! Welcome to Makrana Marble**\n\nI'm here to help you find the perfect marble for your project and provide instant pricing.\n\nTo give you personalized service, may I know your good name please?", 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addBotMessage = (text, delay = 1000) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        text,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString()
      }]);
      setIsTyping(false);
    }, delay);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const calculateMarblePrice = (params) => {
    const {
      marbleType,
      grade,
      thickness,
      area,
      application,
      location,
      customWork
    } = params;

    let calculation = {
      breakdown: {},
      total: 0,
      timeline: 0,
      subtotal: 0,
      tax: 0,
      areaWithWastage: area
    };
    const effectiveArea = Number((area * WASTAGE_FACTOR).toFixed(2));
    calculation.areaWithWastage = effectiveArea;

    // Marble cost calculation
    if (marbleType && marbleDatabase.types[marbleType]) {
      const marble = marbleDatabase.types[marbleType];
      const gradeMultiplier = marble.grades[grade]?.multiplier || 1;
      const thicknessMultiplier = marble.thickness[thickness]?.multiplier || 1;
      
      const marbleCost = marble.basePrice * gradeMultiplier * thicknessMultiplier * effectiveArea;
      calculation.breakdown.marble = {
        description: `${marble.name} (${grade} grade, ${thickness}) with 7% wastage`,
        cost: marbleCost,
        details: `₹${marble.basePrice} × ${gradeMultiplier} × ${thicknessMultiplier} × ${effectiveArea} sq ft`
      };
    }

    // Transport cost
    const transportZone = determineTransportZone(location);
    const transportCost = marbleDatabase.transport.zones[transportZone].rate * area;
    calculation.breakdown.transport = {
      description: `Transport to ${location} (${transportZone} zone)`,
      cost: transportCost,
      details: `₹${marbleDatabase.transport.zones[transportZone].rate} × ${area} sq ft`
    };

    // Labor cost
    const laborRate = marbleDatabase.labor[application]?.rate || 45;
    const laborCost = laborRate * area;
    calculation.breakdown.labor = {
      description: `${application} installation labor`,
      cost: laborCost,
      details: `₹${laborRate} × ${area} sq ft`
    };

    const finishingRate = ['temple', 'kitchen', 'bathroom'].includes(application) ? 25 : 15;
    const finishingCost = finishingRate * area;
    calculation.breakdown.finishing = {
      description: "Cutting, finishing and edge polishing",
      cost: finishingCost,
      details: `₹${finishingRate} × ${area} sq ft`
    };

    // Custom work cost
    if (customWork && customWork.type) {
      const custom = marbleDatabase.customWork[customWork.type];
      if (custom) {
        const complexityMultiplier = custom.complexity[customWork.complexity]?.multiplier || 1;
        const customCost = custom.basePrice * complexityMultiplier * (customWork.area || area * 0.3);
        calculation.breakdown.customWork = {
          description: `${custom.name} (${customWork.complexity} complexity)`,
          cost: customCost,
          details: `₹${custom.basePrice} × ${complexityMultiplier} × ${customWork.area || area * 0.3} sq ft`
        };
      }
    }

    // Volume discount on material component
    if (calculation.breakdown.marble) {
      const discountRate = getVolumeDiscountRate(area);
      if (discountRate > 0) {
        const discountAmount = calculation.breakdown.marble.cost * discountRate;
        calculation.breakdown.discount = {
          description: `Bulk order discount (${Math.round(discountRate * 100)}%)`,
          cost: -discountAmount,
          details: `-₹${discountAmount.toLocaleString()}`
        };
      }
    }

    calculation.subtotal = Object.values(calculation.breakdown).reduce((sum, item) => sum + item.cost, 0);
    calculation.tax = calculation.subtotal * GST_RATE;
    calculation.total = calculation.subtotal + calculation.tax;
    calculation.estimateRange = {
      min: Math.round(calculation.total * 0.95),
      max: Math.round(calculation.total * 1.08)
    };

    // Timeline calculation
    const baseTime = marbleDatabase.projectTimeline[application]?.baseTime || 5;
    calculation.timeline = Math.ceil((area / 100) * baseTime);
    if (customWork && customWork.type) {
      calculation.timeline += Math.ceil(((customWork.area || area * 0.3) / 100) * 10);
    }

    return calculation;
  };

  const determineTransportZone = (location) => {
    const localCities = ['jodhpur', 'jaipur', 'udaipur', 'ajmer', 'bikaner'];
    const regionalCities = ['delhi', 'mumbai', 'ahmedabad', 'indore', 'surat'];
    
    if (localCities.some(city => location.toLowerCase().includes(city))) return 'local';
    if (regionalCities.some(city => location.toLowerCase().includes(city))) return 'regional';
    return 'national';
  };

  const handleUserInput = (message) => {
    addUserMessage(message);
    setInputMessage('');
    
    // Process the message based on current step
    processUserMessage(message.toLowerCase());
  };

  const processUserMessage = (message) => {
    const userInput = message.trim();
    
    switch (calculationStep) {
      case 'welcome':
        // Collect user's name
        setUserProfile(prev => ({ ...prev, name: userInput }));
        setCalculationStep('collectMobile');
        addBotMessage(`Nice to meet you, ${userInput}! 😊\n\nFor better service and to send you detailed quotes, could you please share your mobile number?\n\n*Don't worry - we respect your privacy and won't spam you.*`);
        break;

      case 'collectMobile':
        // Validate and collect mobile number
        const mobileRegex = /^[6-9]\d{9}$/;
        const cleanMobile = userInput.replace(/\D/g, '');
        
        if (mobileRegex.test(cleanMobile)) {
          const updatedProfile = { ...userProfile, mobile: cleanMobile };
          setUserProfile(updatedProfile);
          
          // Save customer data and send notifications
          saveCustomerInquiry(updatedProfile, 'chatbot_registration')
            .then((customerRecord) => {
              console.log('Customer data saved:', customerRecord);
              addBotMessage(`Thank you ${userProfile.name}! 📱 ${cleanMobile}\n\n✅ **Your details have been recorded for personalized service.**\n\n🎯 **I can help you with:**\n\n💰 **Instant Price Calculator**\n• Complete project cost breakdown\n• Material + Transport + Labor costs\n• Timeline estimation\n\n📋 **Expert Guidance**\n• Marble quality recommendations\n• Application suitability advice\n• Maintenance guidelines\n\n🎨 **Custom Work Consultation**\n• Temple carving specialists\n• Inlay work experts\n• Sculpture design\n\nWhat brings you to Makrana Marble today? Are you planning a new project?`);
            })
            .catch((error) => {
              console.error('Failed to save customer data:', error);
              addBotMessage(`Thank you ${userProfile.name}! 📱 ${cleanMobile}\n\n🎯 **I can help you with:**\n\n💰 **Instant Price Calculator**\n• Complete project cost breakdown\n• Material + Transport + Labor costs\n• Timeline estimation\n\n📋 **Expert Guidance**\n• Marble quality recommendations\n• Application suitability advice\n• Maintenance guidelines\n\n🎨 **Custom Work Consultation**\n• Temple carving specialists\n• Inlay work experts\n• Sculpture design\n\nWhat brings you to Makrana Marble today? Are you planning a new project?`);
            });
          
          setCalculationStep('greeting');
        } else {
          addBotMessage("Please enter a valid 10-digit mobile number (starting with 6, 7, 8, or 9).\n\nExample: 9876543210");
        }
        break;

      case 'greeting':
        if (message.includes('price') || message.includes('cost') || message.includes('quote') || message.includes('calculate')) {
          setCalculationStep('projectType');
          addBotMessage(`Perfect ${userProfile.name}! Let's calculate the exact pricing for your project. 📊\n\n**What type of project are you planning?**\n\n🏠 **1. Flooring** (Home, office, commercial)\n🏛️ **2. Temple Construction** (Traditional carving work)\n🍽️ **3. Kitchen Countertop** (Premium surfaces)\n🛁 **4. Bathroom** (Walls, flooring, vanity)\n🏢 **5. Exterior Cladding** (Building facades)\n🎨 **6. Custom Carving Work** (Sculptures, artistic pieces)\n\nPlease type the number (1-6) or describe your project:`);
        } else if (message.includes('carving') || message.includes('sculpture') || message.includes('custom') || message.includes('temple carving')) {
          addBotMessage(`🎨 **Excellent choice ${userProfile.name}!** Custom carving is our specialty!\n\nOur master craftsmen create:\n• Traditional temple carvings\n• Intricate sculptures\n• Marble inlay work (Taj Mahal style)\n• Architectural elements\n• Religious statues\n\n**For custom work, our artisans need to understand your vision personally.**\n\n� **Please contact our expert team:**\n• **Call**: +91-7014351132\n• **WhatsApp**: +91-7014351132\n• **Your details**: ${userProfile.name} - ${userProfile.mobile}\n\nThey'll discuss your requirements and provide detailed estimates.\n\nWould you also like pricing for any standard marble work?`);
        } else if (message.includes('quality') || message.includes('grade') || message.includes('type')) {
          addBotMessage(`Great question ${userProfile.name}! Let me explain our marble qualities:\n\n🏆 **Premium Grade Makrana Marble**\n• Same quality as used in Taj Mahal\n• Minimal veining, pure white color\n• Perfect for luxury projects\n• Price: ₹225/sq ft\n\n⭐ **Standard Grade Makrana Marble**\n• Excellent quality with natural variations\n• Most popular choice\n• Great value for money\n• Price: ₹150/sq ft\n\n💼 **Commercial Grade Makrana Marble**\n• Good quality for large projects\n• More economical option\n• Durable and reliable\n• Price: ₹120/sq ft\n\n**We also have:**\n🌸 **Pink Marble**: ₹180-252/sq ft\n💚 **Green Marble**: ₹200-260/sq ft\n\nWhich grade interests you for your project?`);
        } else if (message.includes('delivery') || message.includes('transport') || message.includes('shipping')) {
          addBotMessage(`📦 **Delivery & Transport Information:**\n\n**Transport Zones & Costs:**\n🚛 **Local** (50km): ₹15/sq ft\n• Jodhpur, Jaipur, Udaipur, Ajmer\n\n🚚 **Regional** (200km): ₹25/sq ft\n• Delhi, Mumbai, Ahmedabad, Surat\n\n🚐 **National** (1000km): ₹40/sq ft\n• Bangalore, Chennai, Kolkata\n\n✈️ **International**: ₹80/sq ft + customs\n\n**Delivery Time:**\n• Local: 2-3 days\n• Regional: 5-7 days\n• National: 7-10 days\n\n**Packaging:** Professional crating for safe transport\n\nWhere is your project location ${userProfile.name}?`);
        } else {
          addBotMessage(`I understand you're interested in marble, ${userProfile.name}! I'm here to help with all your queries.\n\n**Popular topics I can help with:**\n💰 Project pricing and quotes\n📏 Marble specifications and grades\n🚚 Delivery and transport information\n🛠️ Installation guidelines\n🎨 Custom carving consultation\n📞 Connect with our experts\n\nWhat specific information would you like about Makrana marble?`);
        }
        break;

      case 'projectType':
        let applicationType = '';
        if (message.includes('1') || message.includes('floor')) applicationType = 'flooring';
        else if (message.includes('2') || message.includes('temple')) applicationType = 'temple';
        else if (message.includes('3') || message.includes('kitchen')) applicationType = 'kitchen';
        else if (message.includes('4') || message.includes('bathroom')) applicationType = 'bathroom';
        else if (message.includes('5') || message.includes('exterior')) applicationType = 'exterior';
        else if (message.includes('6') || message.includes('custom') || message.includes('carving')) {
          addBotMessage(`🎨 **Custom Carving Consultation Required**\n\nFor custom carving work, our master craftsmen need to discuss your specific requirements:\n\n📞 **Contact Details:**\n• **Name**: ${userProfile.name}\n• **Mobile**: ${userProfile.mobile}\n• **Call/WhatsApp**: +91-7014351132\n\nOur artisans will discuss your vision and provide detailed quotes.\n\nWould you like pricing for any other marble work?`);
          setCalculationStep('greeting');
          return;
        }

        if (applicationType) {
          setCurrentCalculation(prev => ({ ...prev, application: applicationType }));
          setCalculationStep('marbleType');
          const applicationName = applicationType.charAt(0).toUpperCase() + applicationType.slice(1);
          addBotMessage(`Excellent choice ${userProfile.name}! **${applicationName}** is a popular application for Makrana marble.\n\n**Which type of Makrana marble would you prefer?**\n\n🤍 **1. Makrana White Marble** (₹150-225/sq ft)\n• Same as Taj Mahal marble\n• Pure white with minimal veining\n• Perfect for ${applicationType}\n\n🌸 **2. Makrana Pink Marble** (₹180-252/sq ft)\n• Beautiful pink hue\n• Elegant and unique\n• Great for decorative work\n\n💚 **3. Makrana Green Marble** (₹200-260/sq ft)\n• Rare and exclusive\n• Stunning green color\n• Premium choice\n\nPlease select 1, 2, or 3:`);
        } else {
          addBotMessage("Please select a valid option (1-6) or describe your project type clearly.\n\nFor example: 'flooring for my home' or 'temple construction'");
        }
        break;

      case 'marbleType':
        let selectedMarble = '';
        if (message.includes('1') || message.includes('white')) selectedMarble = 'makrana_white';
        else if (message.includes('2') || message.includes('pink')) selectedMarble = 'makrana_pink';
        else if (message.includes('3') || message.includes('green')) selectedMarble = 'makrana_green';

        if (selectedMarble) {
          setCurrentCalculation(prev => ({ ...prev, marbleType: selectedMarble }));
          setCalculationStep('grade');
          const marble = marbleDatabase.types[selectedMarble];
          const gradeOptions = Object.entries(marble.grades)
            .map(([key, value], index) => `**${index + 1}. ${key.charAt(0).toUpperCase() + key.slice(1)} Grade** - ₹${(marble.basePrice * value.multiplier).toLocaleString()}/sq ft\n   ${value.description}`)
            .join('\n\n');
          addBotMessage(`**Excellent choice ${userProfile.name}!** ${marble.name} is perfect for your ${currentCalculation.application} project.\n\n**Select the quality grade:**\n\n${gradeOptions}\n\nWhich grade would you prefer?`);
        } else {
          addBotMessage("Please select a valid marble type (1, 2, or 3) or type 'white', 'pink', or 'green'.");
        }
        break;

      case 'grade':
        const marbleType = marbleDatabase.types[currentCalculation.marbleType];
        const grades = Object.keys(marbleType.grades);
        let selectedGrade = '';
        
        if (message.includes('1')) selectedGrade = grades[0];
        else if (message.includes('2')) selectedGrade = grades[1];
        else if (message.includes('3')) selectedGrade = grades[2];

        if (selectedGrade) {
          setCurrentCalculation(prev => ({ ...prev, grade: selectedGrade }));
          setCalculationStep('thickness');
          const thicknessOptions = Object.entries(marbleType.thickness)
            .map(([ thickness, data], index) => `**${index + 1}. ${thickness}** - ${data.multiplier}x base price`)
            .join('\n');
          addBotMessage(`**Grade selected: ${selectedGrade.charAt(0).toUpperCase() + selectedGrade.slice(1)}** ✅\n\nNow choose the marble thickness:\n\n${thicknessOptions}\n\n*Thicker marble is more durable and suitable for heavy-duty applications.*\n\nWhich thickness do you prefer?`);
        } else {
          addBotMessage("Please select a valid grade option (1, 2, or 3).");
        }
        break;

      case 'thickness':
        const thicknesses = Object.keys(marbleDatabase.types[currentCalculation.marbleType].thickness);
        let selectedThickness = '';
        
        if (message.includes('1')) selectedThickness = thicknesses[0];
        else if (message.includes('2')) selectedThickness = thicknesses[1];
        else if (message.includes('3')) selectedThickness = thicknesses[2];
        else if (message.includes('4')) selectedThickness = thicknesses[3];

        if (selectedThickness) {
          setCurrentCalculation(prev => ({ ...prev, thickness: selectedThickness }));
          setCalculationStep('area');
          addBotMessage(`**Thickness selected: ${selectedThickness}** ✅\n\nNow ${userProfile.name}, what's the total area of your ${currentCalculation.application} project?\n\n**Please enter the area in square feet:**\n\n*Examples:*\n• Small room: 200 sq ft\n• Medium room: 500 sq ft  \n• Large hall: 1000 sq ft\n\nJust type the number (e.g., 500)`);
        } else {
          addBotMessage("Please select a valid thickness option (1, 2, 3, or 4).");
        }
        break;

      case 'area':
        const areaMatch = message.match(/\d+(\.\d+)?/);
        if (areaMatch) {
          const area = parseFloat(areaMatch[0]);
          if (area > 0 && area < 100000) {
            setCurrentCalculation(prev => ({ ...prev, area }));
            setCalculationStep('location');
            addBotMessage(`**Area confirmed: ${area.toLocaleString()} sq ft** ✅\n\nLast question ${userProfile.name}! What's your project location?\n\nThis helps us calculate accurate transport costs.\n\n**Examples:**\n• Delhi\n• Mumbai  \n• Jaipur\n• Bangalore\n• Your city name\n\n*We deliver across India and internationally.*`);
          } else {
            addBotMessage("Please enter a reasonable area between 1 and 99,999 square feet.");
          }
        } else {
          addBotMessage("Please enter a valid area in square feet. Just type the number (e.g., 500).");
        }
        break;

      case 'location':
        setCurrentCalculation(prev => ({ ...prev, location: message }));
        
        // Generate final calculation
        const finalCalculation = calculateMarblePrice({
          ...currentCalculation,
          location: message
        });

        // Save project details to customer record
        const projectDetails = {
          type: currentCalculation.application,
          marbleType: currentCalculation.marbleType,
          grade: currentCalculation.grade,
          thickness: currentCalculation.thickness,
          area: currentCalculation.area,
          location: message,
          totalCost: finalCalculation.total,
          breakdown: finalCalculation.breakdown,
          timeline: finalCalculation.timeline
        };

        // Update customer record with project details
        saveCustomerInquiry({
          ...userProfile,
          projectDetails: JSON.stringify(projectDetails),
          estimatedBudget: finalCalculation.total
        }, 'pricing_inquiry')
          .then((customerRecord) => {
            console.log('Project details saved:', customerRecord);
          })
          .catch((error) => {
            console.error('Failed to save project details:', error);
          });

        let response = `📊 **Complete Quote for ${userProfile.name}**\n\n`;
        response += `📋 **Project Details:**\n`;
        response += `• Type: ${currentCalculation.application.charAt(0).toUpperCase() + currentCalculation.application.slice(1)}\n`;
        response += `• Area: ${currentCalculation.area.toLocaleString()} sq ft\n`;
        response += `• Chargeable marble area (incl. wastage): ${finalCalculation.areaWithWastage.toLocaleString()} sq ft\n`;
        response += `• Location: ${message}\n\n`;
        response += `💰 **Detailed Cost Breakdown:**\n\n`;
        
        Object.entries(finalCalculation.breakdown).forEach(([key, value]) => {
          response += `**${value.description}**\n`;
          response += `   ₹${value.cost.toLocaleString()}\n`;
          response += `   (${value.details})\n\n`;
        });
        
        response += `**Subtotal**: ₹${Math.round(finalCalculation.subtotal).toLocaleString()}\n`;
        response += `**GST (5%)**: ₹${Math.round(finalCalculation.tax).toLocaleString()}\n`;
        response += `🏆 **Total Project Investment: ₹${Math.round(finalCalculation.total).toLocaleString()}**\n`;
        response += `📌 **Expected final range**: ₹${finalCalculation.estimateRange.min.toLocaleString()} - ₹${finalCalculation.estimateRange.max.toLocaleString()}\n`;
        response += `⏱️ **Estimated Completion: ${finalCalculation.timeline} working days**\n\n`;
        response += `📞 **Next Steps:**\n`;
        response += `• **Your Details**: ${userProfile.name} - ${userProfile.mobile}\n`;
        response += `• **Call/WhatsApp**: +91-7014351132\n`;
        response += `• **Quote Valid**: 15 days\n`;
        response += `• **Advance**: 30% to confirm order\n\n`;
        response += `💡 **Additional Services Available:**\n`;
        response += `• Site visit for measurement\n`;
        response += `• Professional installation\n`;
        response += `• 2-year workmanship warranty\n\n`;
        response += `Would you like a quote for any other project ${userProfile.name}?`;

        addBotMessage(response, 2000);
        
        // Reset for next calculation but keep user profile
        setCalculationStep('greeting');
        setCurrentCalculation({});
        break;

      default:
        addBotMessage("I'm here to help with marble pricing and specifications. Type 'calculate price' to get a quote or ask me any questions about Makrana marble!");
    }
  };

  const quickActions = [
    { 
      text: "Get Price Quote", 
      action: () => {
        if (calculationStep === 'greeting' && userProfile.name && userProfile.mobile) {
          handleUserInput("I want to calculate price for my project");
        } else {
          addBotMessage("Please complete the introduction first by providing your name and mobile number.");
        }
      }
    },
    { 
      text: "Marble Quality Info", 
      action: () => {
        if (userProfile.name) {
          handleUserInput("Tell me about marble quality and grades");
        } else {
          addBotMessage("Please tell me your name first so I can assist you better.");
        }
      }
    },
    { 
      text: "Custom Carving", 
      action: () => {
        if (userProfile.name) {
          handleUserInput("I need custom carving work for temple");
        } else {
          addBotMessage("Please introduce yourself first - I'd like to know your name.");
        }
      }
    },
    { 
      text: "Delivery Info", 
      action: () => {
        if (userProfile.name) {
          handleUserInput("What are delivery charges and timeline");
        } else {
          addBotMessage("Hi! May I know your name first? Then I can help with delivery information.");
        }
      }
    }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] sm:h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 rounded-t-2xl sm:rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                🤖
              </div>
              <div>
                <h3 className="font-bold">Makrana Marble AI Assistant</h3>
                <p className="text-sm opacity-90">Instant pricing & expert guidance</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs sm:max-w-md px-4 py-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="whitespace-pre-line text-sm">{message.text}</div>
                  <div className={`text-xs mt-1 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {message.timestamp}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 px-4 py-3 rounded-2xl">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t">
            <div className="flex flex-wrap gap-2 mb-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  {action.text}
                </button>
              ))}
            </div>

            {/* Input Area */}
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && inputMessage.trim() && handleUserInput(inputMessage)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                onClick={() => inputMessage.trim() && handleUserInput(inputMessage)}
                disabled={!inputMessage.trim()}
                className="px-6 py-3 bg-orange-600 text-white rounded-full hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIMarbleChatbot;
